import { PropsWithChildren } from 'react';

import { useUser } from '@auth-v2';
import { useAtomValue } from 'jotai';

import { currentActivityAtom, layoutAtom } from '@store';
import { Box, Heading2, Heading6, Inline, u } from '@ui';

import { useComparisonSwingName } from '../../hooks/useComparisonName';

export const User = () => {
    const { data: user } = useUser();
    return user && <PillSection heading="user" label={user?.user_metadata?.['name'] ?? ''} />;
};

export const Layout = () => {
    const activeLayout = useAtomValue(layoutAtom);
    const selectedLayout = activeLayout?.name ?? 'No layout';
    return selectedLayout && <PillSection heading="Layout" label={selectedLayout} />;
};

export const Club = () => {
    const activity = useAtomValue(currentActivityAtom);
    const club = activity?.club?.shortName || '';
    return club && <PillSection heading="Club" label={club} />;
};

export const Tags = () => {
    const activity = useAtomValue(currentActivityAtom);
    const tags = activity?.tags?.map(({ name }) => name) || [];
    const visibleTags = tags.slice(0, 3);
    const remainingTagCount = tags.length - visibleTags.length;
    const remainingTagLabel = remainingTagCount === 1 ? tags.at(-1) ?? '' : `+${remainingTagCount} more`;

    return (
        tags.length !== 0 && (
            <PillHeading heading="Tags">
                {visibleTags.map((tag) => (
                    <Pill key={tag} label={tag} />
                ))}
                {remainingTagCount > 0 && <Pill key="remaining-tags" label={remainingTagLabel} />}
            </PillHeading>
        )
    );
};

export const ComparisonSwing = () => {
    const { comparisonName, comparisonSwing, showComparison } = useComparisonSwingName();

    return comparisonSwing && <PillSection heading="Comparison" label={comparisonName} isActive={showComparison} />;
};

type PillHeadingProps = { heading: string };
type PillProps = { label: string; isActive?: boolean };
const PillSection = ({ heading, isActive, label }: PillHeadingProps & PillProps) => (
    <PillHeading heading={heading}>
        <Pill label={label} isActive={isActive} />
    </PillHeading>
);

const PillHeading = ({ heading, children }: PropsWithChildren<PillHeadingProps>) => (
    <Inline alignItems="center" gap={u(2)}>
        <Heading6 color="muted">{heading}</Heading6>
        {children}
    </Inline>
);

const Pill = ({ label, isActive = true }: PillProps) => (
    <Box bg="emphasis" radius="pill" px={u(2)} py={u(1)}>
        <Heading2 weight={700} color={isActive ? 'primary' : 'muted'}>
            {label}
        </Heading2>
    </Box>
);
